import React, {
	useState,
	useContext,
	useCallback,
	useMemo,
} from 'react';
import { userAxios } from '../userAxios';
import { UserContext } from './UserProvider';

const API_URL = process.env.REACT_APP_API_URL;
export const SettingsContext = React.createContext();

const SettingsProvider = (props) => {
	const { token } = useContext(UserContext);
	const [archiveData, setArchiveData] = useState([]);
	const [notificationPreferences, setNotificationPreferences] = useState([]);
	const [vendorTypes, setVendorTypes] = useState([]);
	const [feedTypes, setFeedTypes] = useState([]);
	const [fileTypes, setFileTypes] = useState([]);
	const [resources, setResources] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [debounce, setDebounce] = useState(false);
	const [user, setUser] = useState(null);
	const [termsOfService, setTermsOfService] = useState('');

	const getTermsOfService = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/settings/tos`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setTermsOfService(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const updateTermsOfService = useCallback(
		async (updatedTerms) => {
			try {
				const response = await userAxios.put(
					`${API_URL}/settings/tos`,
					updatedTerms,
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);
				setTermsOfService(response.data);
			} catch (error) {
				console.error(error);
			}
		},
		[token]
	);

	const refreshTermsOfService = useCallback(
		async (updatedTerms) => {
			try {
				await userAxios.post(
					`${API_URL}/settings/tos/refresh`,
					updatedTerms,
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);
				console.log("Terms of Service refresh request sent successfully.");
			} catch (error) {
				console.error("Failed to refresh Terms of Service:", error);
			}
		},
		[token]
	);

	const getUser = async () => {
		try {
			const response = await userAxios.get(API_URL + '/profile', {
				headers: { Authorization: `Bearer ${token}` },
			});
			setUser(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const updateUser = async (data, verify = false) => {
		try {
			const payload = {
				username: data.username,
			};

			if (data.password) {
				payload.newPassword = data.password;
				if (verify && data.currentPassword) {
					payload.currentPassword = data.currentPassword;
				} else if (verify) {
					throw new Error('Current password is required for verification');
				}
			}

			const response = await userAxios.put(
				`${API_URL}/profile`,
				payload,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);

			setUser(response.data);
			return response.data;
		} catch (error) {
			if (error.response?.status === 401) {
				throw new Error('Current password is incorrect');
			}
			if (error.response?.status === 400) {
				throw new Error('Invalid request data');
			}
			console.error('Error updating user:', error);
			throw error;
		}
	};

	const resetPassword = async (updatedUserData, tokenOverride = null) => {
		const t = tokenOverride ? tokenOverride : token;
		try {
			await userAxios.put(`${API_URL}/profile/resetpassword`, updatedUserData, {
				headers: { Authorization: `Bearer ${t}` },
			});
			setUser(updatedUserData);
		} catch (error) {
			console.log('Stored Token:', localStorage.getItem('resetToken'));
			console.error('Error Details:', error.response?.data || error.message);
			console.error('Error updating user:', error);
			throw error;
		}
	};

	const getArchiveData = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/vendors/archived`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setArchiveData(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const updateArchiveData = useCallback(
		(rowKey) => {
			let vendor = archiveData.find((v) => v.rowKey === rowKey);
			vendor.archived = false;
			userAxios
				.put(`${API_URL}/vendors`, vendor, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then(() => {
					getArchiveData();
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[archiveData, token, getArchiveData]
	);

	const getVendorTypes = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/admin/vendortypes`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setVendorTypes(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const postVendorTypes = useCallback(
		(newVendorTypeObject) => {
			if (newVendorTypeObject.type.trim().length === 0) {
				alert('Please enter a value before submitting.');
				return Promise.reject('Type cannot be empty');
			}
			return userAxios
				.post(`${API_URL}/admin/vendortypes`, newVendorTypeObject, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then((response) => {
					setVendorTypes((prevVendorTypes) => [
						...prevVendorTypes,
						response.data,
					]);
					getVendorTypes();
				})
				.catch((error) => {
					console.error(error);
					alert('Failed to create vendor type');
				});
		},
		[token, getVendorTypes]
	);

	const updateVendorType = useCallback(
		(updatedVendorType) => {
			if (debounce) return;
			setDebounce(true);
			userAxios
				.put(API_URL + '/admin/vendortypes', updatedVendorType, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then(() => {
					setDebounce(false);
				})
				.catch((error) => {
					console.error(error);
					setDebounce(false);
				});
		},
		[debounce, token]
	);

	const deleteVendorType = useCallback(
		(id) => {
			return userAxios
				.delete(API_URL + '/admin/vendortypes/' + id, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then((response) => {
					return response.data;
				})
				.catch((error) => {
					console.error(error);
					throw new Error('Failed to delete vendor type');
				});
		},
		[token]
	);

	const getFeedTypes = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/admin/feedtypes`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setFeedTypes(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const postFeedTypes = useCallback(
		(newFeedTypeObject) => {
			if (newFeedTypeObject.type.trim().length === 0) {
				alert('Please enter a value before submitting.');
				return Promise.reject('Type cannot be empty');
			}
			return userAxios
				.post(`${API_URL}/admin/feedtypes`, newFeedTypeObject, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then((response) => {
					setFeedTypes((prevFeedTypes) => [...prevFeedTypes, response.data]);
					getFeedTypes();
				})
				.catch((error) => {
					console.error(error);
					alert('Failed to create feed type');
				});
		},
		[token, getFeedTypes]
	);

	const updateFeedType = useCallback(
		(updatedFeedType) => {
			if (debounce) return;
			setDebounce(true);
			userAxios
				.put(API_URL + '/admin/feedtypes', updatedFeedType, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then(() => {
					setDebounce(false);
				})
				.catch((error) => {
					console.error(error);
					setDebounce(false);
				});
		},
		[debounce, token]
	);

	const deleteFeedType = useCallback(
		(id) => {
			return userAxios
				.delete(API_URL + '/admin/feedtypes/' + id, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then((response) => {
					return response.data;
				})
				.catch((error) => {
					console.error(error);
					throw new Error('Failed to delete feed type');
				});
		},
		[token]
	);

	const getFileTypes = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/admin/documenttypes`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setFileTypes(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const postFileTypes = useCallback(
		(newFileType) => {
			return userAxios
				.post(API_URL + '/admin/documenttypes', newFileType, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then((response) => {
					setFileTypes((prevFileTypes) => [...prevFileTypes, response.data]);
					getFileTypes();
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[token]
	);

	const updateFileType = useCallback(
		(updatedFileType) => {
			if (debounce) return;
			setDebounce(true);
			userAxios
				.put(API_URL + '/admin/documenttypes', updatedFileType, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then(() => {
					setDebounce(false);
				})
				.catch((error) => {
					console.error(error);
					setDebounce(false);
				});
		},
		[debounce, token]
	);

	const deleteFileType = useCallback(
		(id) => {
			return userAxios
				.delete(`${API_URL}/admin/documenttypes/${id}`, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then((response) => {
					getFileTypes();
					return response.data;
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[token, getFileTypes]
	);

	const getResources = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/resources`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setResources(response.data);
		} catch (error) {
			console.error(error);
		}
	}, [token]);

	const postResourceFile = async (formData) => {
		try {
			const response = await userAxios.post(`${API_URL}/resources`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: `Bearer ${token}`,
				},
			});
			setResources([...resources, response.data]);
			getResources();
		} catch (error) {
			console.error('Error uploading file:', error);
		}
	};

	const postResourceLink = useCallback(
		(newResource) => {
			const data = {
				name: newResource.name,
				link: newResource.content,
			};
			const headers = {
				'Content-Type': 'application/json',
				Authorization: 'Bearer ' + token,
			};
			return userAxios
				.post(`${API_URL}/resources`, data, { headers })
				.then((response) => {
					setResources([...resources, response.data]);
					getResources();
				})
				.catch((error) => {
					console.error('Error:', error);
				});
		},
		[resources, getResources, token]
	);

	const updateResource = useCallback(() => {
		const updatedResources = resources.map((resource, index) => {
			return { resource: resource, order: index };
		});
		if (debounce) return;
		setDebounce(true);
		userAxios
			.put(API_URL + '/resources', updatedResources, {
				headers: {
					Authorization: 'Bearer ' + token,
				},
			})
			.then(() => {
				setDebounce(false);
			})
			.catch((error) => {
				console.error(error);
				setDebounce(false);
			});
	}, [resources, debounce, token]);

	const deleteResource = useCallback(
		(id) => {
			userAxios
				.delete(API_URL + '/resources/' + id, {
					headers: {
						Authorization: 'Bearer ' + token,
					},
				})
				.then((response) => {
					getResources();
					return response.data;
				})
				.catch((err) => {
					console.error(err);
				});
		},
		[token, getResources]
	);

	const getStaffAccountData = async () => {
		try {
			const response = await userAxios.get(API_URL + '/users', {
				headers: { Authorization: `Bearer ${token}` },
			});
			setAccounts(
				response.data.map((account) => {
					return {
						...account,
						role: roleText(account.role),
					};
				})
			);
		} catch (error) {
			console.error(error);
		}
	};

	const inviteUser = useCallback(
		async (inviteData) => {
			try {
				const response = await userAxios.post(`${API_URL}/users/accounts`, inviteData, {
					headers: { Authorization: `Bearer ${token}` },
				});
				setAccounts((prev) => [...prev, response.data]);
				return response;
			} catch (error) {
				console.error('Error inviting user:', error);
				throw error;
			}
		},
		[token, setAccounts]
	);

	const createStaffAccount = useCallback(
		async (staffData) => {
			try {
				const role = staffData.role === 'Admin' ? 1 : 2;
				const dataToSend = {
					...staffData,
					role,
				};
				const response = await userAxios.post(`${API_URL}/users`, dataToSend, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});
				return response.data;
			} catch (error) {
				console.error('Error creating staff account:', error);
				throw error;
			}
		},
		[token]
	);

	const deleteStaffAccount = useCallback(
		async (id) => {
			try {
				const response = await userAxios.delete(`${API_URL}/users/${id}`, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});
				return response.data;
			} catch (error) {
				console.error('Error deleting staff account:', error);
				throw error;
			}
		},
		[token]
	);

	const updateStaffAccount = useCallback(
		async (id, staffData) => {
			try {
				const dataToSend = {
					...staffData,
				};
				const response = await userAxios.put(
					`${API_URL}/users/${id}`,
					dataToSend,
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);
				return response.data;
			} catch (error) {
				console.error('Error updating staff account:', error);
				throw error;
			}
		},
		[token]
	);

	const roleText = (role) => {
		if (role === 1 || role === 'Admin') {
			return 'Admin';
		} else if (role === 2 || role === 'Staff') {
			return 'Staff';
		}
		return 'Unknown';
	};

	const getNotificationPreferences = async () => {
		try {
			const response = await userAxios.get(
				`${API_URL}/profile/preferences/notifications`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			setNotificationPreferences(response.data);
			return response.data;
		} catch (error) {
			console.error(error);
		}
	};

	const updateNotificationPreferences = async (updatedPreferences) => {
		try {
			const response = await userAxios.put(
				`${API_URL}/profile/preferences/notifications`,
				updatedPreferences,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			setNotificationPreferences(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getOfficeSyncRecords = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/officesync`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			return response.data;
		} catch (error) {
			console.error('Error fetching sync records:', error);
			throw error;
		}
	}, [token]);

	const getMemberSyncRecords = useCallback(async () => {
		try {
			const response = await userAxios.get(`${API_URL}/membersync`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			return response.data;
		} catch (error) {
			console.error('Error fetching sync records:', error);
			throw error;
		}
	}, [token]);

	const executeSync = useCallback(
		async (syncType, keys) => {
			const endpoint = syncType === 'Member' ? 'membersync' : 'officesync';
			const params = new URLSearchParams();

			if (keys.trim()) {
				params.append(syncType === 'Member' ? 'memberkeys' : 'officekeys', keys.trim());
			} else {
				params.append('scope', 'incremental');
			}

			try {
				const response = await userAxios.post(`${API_URL}/${endpoint}?${params}`, null, {
					headers: { Authorization: `Bearer ${token}` },
				});
				return { success: true, data: response.data };
			} catch (error) {
				console.error(`Error executing ${syncType} sync:`, error);
				return { success: false };
			}
		},
		[token]
	);

	const executeFullSync = useCallback(
		async (syncType) => {
			const endpoint = syncType === 'Member' ? 'membersync' : 'officesync';
			const params = new URLSearchParams();
			params.append('scope', 'full');

			try {
				const response = await userAxios.post(`${API_URL}/${endpoint}?${params}`, null, {
					headers: { Authorization: `Bearer ${token}` },
				});
				return { success: true, data: response.data };
			} catch (error) {
				console.error(`Error executing full ${syncType} sync:`, error);
				return { success: false };
			}
		},
		[token]
	);

	return (
		<SettingsContext.Provider
			value={useMemo(
				() => ({
					archiveData,
					setArchiveData,
					vendorTypes,
					setVendorTypes,
					feedTypes,
					setFeedTypes,
					fileTypes,
					setFileTypes,
					resources,
					setResources,
					accounts,
					user,
					setUser,
					termsOfService,
					setTermsOfService,
					getTermsOfService,
					updateTermsOfService,
					getUser,
					updateUser,
					resetPassword,
					getArchiveData,
					updateArchiveData,
					getVendorTypes,
					postVendorTypes,
					updateVendorType,
					deleteVendorType,
					getFeedTypes,
					postFeedTypes,
					updateFeedType,
					deleteFeedType,
					getFileTypes,
					postFileTypes,
					updateFileType,
					deleteFileType,
					getResources,
					postResourceFile,
					postResourceLink,
					updateResource,
					deleteResource,
					getStaffAccountData,
					createStaffAccount,
					deleteStaffAccount,
					updateStaffAccount,
					notificationPreferences,
					setNotificationPreferences,
					getNotificationPreferences,
					updateNotificationPreferences,
					roleText,
					inviteUser,
					refreshTermsOfService,
					getMemberSyncRecords,
					getOfficeSyncRecords,
					executeSync,
					executeFullSync
				}),
				[
					archiveData,
					vendorTypes,
					feedTypes,
					fileTypes,
					resources,
					accounts,
					user,
					termsOfService,
					getTermsOfService,
					updateTermsOfService,
				]
			)}
		>
			{props.children}
		</SettingsContext.Provider>
	);
};

export default SettingsProvider;